import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Observable } from 'rxjs';
import { IAddress } from '@signal/asp-data-commons';
import { Store } from '@ngrx/store';
import { StateDetails } from 'src/app/modules/shared/models/state';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { countryCd, unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { DealerState } from 'src/app/modules/store/dealer/dealer.reducer';
import { dealerDetailsData, selectCities, selectStates } from 'src/app/modules/store/dealer/dealer.selectors';
import { ServiceAppointmentState } from 'src/app/modules/store/service-appointment/service-appointment.reducer';
import { selectStatesAndCitiesBasedOnZip } from 'src/app/modules/store/service-appointment/service-appointment.selectors';
import { LoadStatesAndCities } from 'src/app/modules/store/service-appointment/service-appointment.actions';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerInfoComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomerInfoComponent),
      multi: true
    }
  ]
})
export class CustomerInfoComponent implements ControlValueAccessor, OnDestroy, OnChanges, OnInit {
  @Input() location: string;
  @Input() department: string;
  @Input() remoteValidZipcodes: { pmaZipCodes: string[], isRemote: boolean };
  @Output() formEdited:EventEmitter<boolean> = new EventEmitter();
  isZipValid:boolean=false;
  customerInfo: UntypedFormGroup;
  hoursOfOperation$: Observable<string>;
  dealerName$: Observable<string>;
  dealerPhone$: Observable<string>;
  dealerAddress$: Observable<string>;
  amenities$: Observable<string[]>;
  states$: Observable<StateDetails[]>;
  cities$: Observable<{ [key: string]: string[] }>;
  signedIn: boolean;
  subscriptions: SubscriptionList = {};
  stateName: string;
  otherStateName: string;
  lastLocation;
  preferredCountries: string = this.translate.currentLang ? this.translate.currentLang.split('-')[1].toLowerCase(): 'us';
  address:{
    addressLine1:'',
    addressLine2:'',
    state:'',
    city:'',
    zip:''
  }
  dealerDetailsData$;
  dealerAddressForRadio;
  addressValidationList = ['addressLine1', 'city', 'state'];
  formStates = {
    "address": { states: [] }
  }
  formcities = {
    "address": { cities: { statecities: {} } }
  }
  stateCitites:{
    formStates,
    formcities
  }
  dealerPhoneNumber;
  remoteAddress="";

  onChange = (value: IAddress) => { };
  onTouched = () => { };

  get value(): IAddress {
    return this.customerInfo.value;
  }

  set value(value: IAddress) {
    this.customerInfo.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get addressControl() {
    return this.customerInfo as UntypedFormGroup;
  }



  ngOnChanges(changes: SimpleChanges): void {
    /**auto select correct address on the basis of transportation typeCode */
    if( changes?.location?.currentValue==="REMOTE" && changes?.location?.previousValue==="DEALER") {
      this.customerInfo.patchValue({
              addressLine1: '',
              addressLine2: '',
              city: '',
              state: '',
              zip: '',
              country: countryCd
            });
    }

    if(this.location && changes?.location?.currentValue==="DEALER" && changes.location.currentValue !== changes.location.previousValue) {
      if(changes?.location?.previousValue==="REMOTE") {
        this.remoteAddress=this.customerInfo.value;
      }
      let dealerAddress = {...this.dealerAddressForRadio.address};
      if(!dealerAddress.addressLine2) {
        dealerAddress.addressLine2 = "";
      }
      this.selectDealerAddress(dealerAddress);
    }
    
    if(this.location && changes?.location?.currentValue==="REMOTE" && changes.location.currentValue !== changes.location.previousValue) {
      if(!this.remoteAddress) {
        this.remoteAddress = this.customerInfo.value;
      }
      this.selectDealerAddress(this.remoteAddress);
    }

    if (changes.hasOwnProperty('location')) {
      if (this.location === 'REMOTE') {
        this.customerInfo.setValidators(this.chooseVehicleValidator());
        this.customerInfo.updateValueAndValidity();
      }else{
         //this.customerInfo.get('address').reset();
        // this.customerInfo.get('otherAddressChecked').reset(false);
        // this.customerInfo.get('otherAddress').reset();
      }
    }
  }


  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly dealerState: Store<DealerState>,
    private readonly serviceAppointmentState: Store<ServiceAppointmentState>,
    private readonly translate: TranslateService
    ) {

    this.initializeForm();

    this.subscriptions.formChangeSub = this.customerInfo.valueChanges.subscribe(value => {
      this.onChange(value);
      this.onTouched();
      this.formEdited.emit(!this.customerInfo.dirty);
    });

    /* Dealer details from State*/
    this.getStateValue();
  }

  ngOnInit() {
  this.subscriptions.zipChange = this.addressControl.get('zip').valueChanges.subscribe(value=>{
    if(value && value.length===5 && this.location==='REMOTE'){
      this.zipChange(value);
    }
  })
  }

  initializeForm() {
    this.dealerDetailsData$ = this.dealerState.select(dealerDetailsData);
    this.subscriptions.dealerDetails = this.dealerDetailsData$.subscribe(data=>{
      this.dealerAddressForRadio = data;
      this.dealerPhoneNumber = this.formatPhoneNumber(data.phoneNumber);
    })
    this.lastLocation = location;
    this.customerInfo = this.formBuilder.group({
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      state: [''],
      zip: [''],
      country: [countryCd]
    });
  }

  selectDealerAddress(address) {
    this.customerInfo.patchValue(address);
  }

  getStateValue() {
    
    this.states$ = this.dealerState.select(selectStates);
    this.cities$ = this.dealerState.select(selectCities);
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscriptions);
  }

  registerOnChange = (fn: any) => {
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  validate(_: UntypedFormControl) {
    let loop:true
    if (this.location === 'REMOTE') {
      this.customerInfo.setValidators(this.chooseVehicleValidator());
   } else {
       this.reset('address');
    }
    return this.customerInfo.valid ? null : { customer: { valid: false } };
  }

  get f() {
    return this.customerInfo.controls;
  }

  chooseVehicleValidator(): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors => {
      const address = group as UntypedFormGroup;

      if (this.location === 'REMOTE') {
        this.addressValidationList.forEach(item => {
          if (!address.get(item).value) {
            address.get(item).setErrors({ required: `${item} ${this.translate.instant('service.isRequired')}` });
          }
        });
      }
      return;
    };
  }

  reset(type) {
    let address;
    if (type === 'address') {
      address = this.customerInfo as UntypedFormGroup;
    } else {
     
    }
    this.addressValidationList.forEach(item => {
      address.get(item).setErrors(null, { emitEvent: false });
    });
  }

  zipChange(value) {
    if (value && value.length === 5 && this.location === 'REMOTE') {
      this.dispatchZip(value);
      this.getStateCities(value)
    }
  }
  
  formatPhoneNumber(phoneNumberString) {
    let cleaned = phoneNumberString.replace(/\D/g, '');

    if (cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1')) {
        const intlCode = cleaned.length === 11 ? '+1 ' : '';
        const startIndex = cleaned.length === 11 ? 1 : 0;

        return intlCode + '(' + cleaned.slice(startIndex, startIndex + 3) + ') ' + cleaned.slice(startIndex + 3, startIndex + 6) + '-' + cleaned.slice(startIndex + 6);
    }

    return null;
  }

  dispatchZip(value){
    if (value && value.length >= 5) {
        this.serviceAppointmentState.dispatch(new LoadStatesAndCities({ zipPinCode: value }))
    }
  }
  getStateCities(value){
      const props = { zipCode: value };
      this.subscriptions.statesCities = this.serviceAppointmentState.select(selectStatesAndCitiesBasedOnZip,props).subscribe(response => {
        if(response){
          const control='address';
            this.mapZipCodeResponse(response,control);
        }
      });
  }
  private mapZipCodeResponse(response,control) {
    if (response.stateCity && response.stateCity.length > 0 && this.customerInfo.value.zip === response.postalCode) {
      this.isZipValid=true;
      if (response.stateCity.length === 1) {
        this.formStates[control].states = [response.stateCity[0].state];
        this.formcities[control].cities.statecities = response.stateCity;
        if (response.stateCity[0].cities.length === 1) {
          this.addressControl.patchValue(
            {state: response.stateCity[0].state,
             city: response.stateCity[0].cities[0] 
            })
        }
        else{
          this.addressControl.patchValue({ state: response.stateCity[0].state})
        }
      }
      else {
        const states = [];
        let cities = {}
        for (const statecities of response.stateCity) {
          states.push(statecities.state);
          cities = { ...cities, statecities: response.stateCity }
        }
        
        this.formStates[control].states = states;
        this.formcities[control].cities = cities;
      }
    }
    else {
      if (this.customerInfo.value.zip === response.postalCode) {
        this.isZipValid=false;
        this.addressControl.get('zip').setErrors({ required: this.translate.instant('service.invalidZip') });
       this.addressControl.get('zip').setValidators(Validators.required);
      }
    }
  }
  updateAddressFields($event,pickUpAddress){

  }
}
